import { Directive, ElementRef, HostListener, inject, input } from '@angular/core';

/** Allows optional single decimal point (e.g. `12`, `12.`, `12.34`). */
const DECIMALS_PATTERN = /^[0-9]+(\.[0-9]*){0,1}$/;

/**
 * Restricts input to decimal numbers (keypress + paste).
 * Pass `false` to disable without removing the attribute.
 */
@Directive({
    selector: '[ancDecimalsOnly]',
    standalone: true
})
export class AncDecimalsOnlyDirective {
    /** When false, the directive is inert. */
    ancDecimalsOnly = input(true);
    /** Max digits after the decimal point; omit for no limit. */
    ancDecimalsMaxPlaces = input<number | undefined>(undefined);

    private readonly elementRef = inject(ElementRef<HTMLInputElement>);

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        if (!this.ancDecimalsOnly()) {
            return;
        }

        const current = this.elementRef.nativeElement.value;
        const pastedText = event.clipboardData?.getData('text') ?? '';
        const inputText = current.concat(pastedText);

        if (!this.isAllowedDecimalValue(inputText)) {
            event.preventDefault();
        }
    }

    @HostListener('keypress', ['$event'])
    onKeyPress(event: KeyboardEvent): void {
        if (!this.ancDecimalsOnly()) {
            return;
        }

        const current = this.elementRef.nativeElement.value;
        const next = current.concat(event.key);

        if (next && !this.isAllowedDecimalValue(next)) {
            event.preventDefault();
        }
    }

    private isAllowedDecimalValue(value: string): boolean {
        const maxPlaces = this.ancDecimalsMaxPlaces();
        if (maxPlaces == null) {
            return value.match(DECIMALS_PATTERN) != null;
        }

        const limitedPattern = new RegExp(`^\\d+\\.?\\d{0,${maxPlaces}}$`);
        return limitedPattern.test(value);
    }
}
